import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import localFileJobs from '../data/jobs/jobs-1.json';
import {UiJob, RawJob} from '../types/index'
interface Job {
  _id: string;
  hashOfContent: string;
  llmoutput: string;
  [key: string]: any;
}

interface AuthContextType {
  loading: boolean;
  jobs: UiJob[] | null;
  skills: string[] | null;
  getJobs: () => Promise<void>;
  deleteJob: (id: string) => Promise<void>;
  saveJob: (jobData: Partial<Job>) => Promise<void>;
  analyseCV: (cv: string) => Promise<void>;
}

const STORAGE_KEY = 'cachedJobs';
const CACHE_DURATION_MS = 1000 * 60 * 15; // 5 minutes cache freshness
const API_URL = 'https://api.paca-dev.rivieraapps.com/api/';

const ApiContext = createContext<AuthContextType | undefined>(undefined);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<UiJob[] | null>(null);
  const [skills, setSkills] = useState<string[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const expiredJobAgeInDays = 20;

  const isFetchingRef = useRef(false); // prevent concurrent/repeated fetches
  const useLocalJobsFile = false; // set to 'true' to use local jobs file for testing
  function mapRawJobToUiJob(rawJobs: RawJob[]): UiJob[] {
    const result: UiJob[] = [];
    for (const rawJob of rawJobs) {
        // guard - skip the object if there is no llmoutput detail


        if (!rawJob || !Object.hasOwn(rawJob, 'llmoutput') || !rawJob.llmoutput) {
            continue; // skip this item
        }
        const llm = rawJob.llmoutput;
        const updatedAt = new Date(rawJob.updatedAt);
        // Extract/use fields safely, with fallback/defaults as needed
        const uijob =  {
            id: rawJob._id,
            title: llm.joboppeningtitle || llm.name || 'Untitled Job',
            company: llm.joboppeningcompanyname || rawJob.company || 'Unknown Company',
            location: llm.joboppeninglocation || 'Location not specified',
            type: llm.jobposttype || 'N/A',
            salary: llm.salary || 'Salary not specified',
            remote: llm.jobisremote ?? false,
            tags: Array.isArray(llm.joboppeningsearchtags) ? llm.joboppeningsearchtags : [],
            description: llm.joboppeningshortdescription || null,
            longDescription: llm.joboppninglongdescription || null,
            validJobPost: llm.isthisjoboppeningforasinglejobpost || null,
            responsibilities: Array.isArray(llm.jobresponsibilities) ? llm.jobresponsibilities : [],
            skillsRequired: Array.isArray(llm.jobskillrequirements) ? llm.jobskillrequirements : [],
            niceToHave: Array.isArray(llm.jobnicetohaves) ? llm.jobnicetohaves : [],
            languages: Array.isArray(llm.jobspokenlanguages) ? llm.jobspokenlanguages : [],
            programmingLanguages: Array.isArray(llm.jobprogramminglanguages) ? llm.jobprogramminglanguages : [],
            developerTools: Array.isArray(llm.jobdevelopertools) ? llm.jobdevelopertools : [],
            methodologies: Array.isArray(llm.jobdevelopermethodologies) ? llm.jobdevelopermethodologies : [],
            experienceLevel: llm.jobexperiencelevel || null,
            applyUrl: llm.jobwheretoapply || llm.joboppeningurl || null,
            applicationProcess: llm.applicationprocessandcontact || null,
            postedAt: rawJob.updatedAt || null,
            expiresAt: new Date( new Date(rawJob.updatedAt).getTime() + expiredJobAgeInDays*24*60*60*1000 ),
            workConditions: llm.jobworkconditions || null,
            availability: llm.jobavailiability || null
        };
        // guard, avoid jobs
        if ( uijob.title === "Untitled Job" ){  continue; }
        if ( uijob.title.includes("not provided") ){  continue; }
        if ( uijob.responsibilities === null ){  continue; }
        if ( uijob.validJobPost == false) {continue}
        let nullcounter = 0;
        Object.values(uijob).forEach(v=>{v==null? nullcounter+=1 : nullcounter;})
        if( nullcounter > 5){ continue; }
        // if job is old (20d), skip it
        if ( new Date().getTime() - updatedAt.getTime() > ( expiredJobAgeInDays*24*60*60*1000 ) ){
          continue;
        }
        if( llm.isthisjoboppeningforasinglejobpost == false ) {continue;}
        result.push(uijob) 
    };
    return result;
  }


  const getJobs = useCallback(async () => {
    if ( useLocalJobsFile === true ){ 
      localFileJobs.jobs = localFileJobs.jobs.sort((a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      const uijobs = mapRawJobToUiJob(localFileJobs.jobs);
      setJobs(uijobs);
      return;
    }

    console.log("", "Fetching jobs from API...");
    try {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (loading) { isFetchingRef.current = false; return; }

      setLoading(true);

      // Check cached data and freshness
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        try {
          const { timestamp, data } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION_MS) {
            setJobs(data);
            setLoading(false);
            isFetchingRef.current = false;
            return;
          }
        } catch (e) {
          // fallthrough to fetch if cache parse fails
        }
      }

      // Fetch from API
      const response = await fetch(API_URL + 'jobs');
      if (!response.ok) throw new Error('Failed to fetch jobs');
      const json = await response.json();
      const uijobs = mapRawJobToUiJob(json.jobs);
      // Cache the data locally with timestamp
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), data: uijobs }));

      setJobs(uijobs);
    } catch (err) {
      console.error('Failed to get jobs:', err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [loading]);

  const deleteJob = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}jobs/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete job');

      // Update local cache and state
      const updatedJobs = jobs?.filter(job => job.id !== id) || null;
      setJobs(updatedJobs);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), data: updatedJobs }));
    } catch (err) {
      console.error('Failed to delete job:', err);
    } finally {
      setLoading(false);
    }
  }, [jobs]);

  const saveJob = useCallback(async (jobData: Partial<Job>) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-password': 'yourPasswordHere' },
        body: JSON.stringify(jobData),
      });
      if (!response.ok) throw new Error('Failed to save job');

      const json = await response.json();
      // Update local cache and state
      const updatedJobs = jobs ? [...jobs] : [];
      const index = updatedJobs.findIndex(job => job.hashOfContent === json.job.hashOfContent);
      if (index > -1) {
        updatedJobs[index] = json.job;
      } else {
        updatedJobs.push(json.job);
      }
      setJobs(updatedJobs);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), data: updatedJobs }));
    } catch (err) {
      console.error('Failed to save job:', err);
    } finally {
      setLoading(false);
    }
  }, [jobs]);

  const analyseCV = useCallback(async (cv: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}jobs/cvanalyse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-password': 'yourPasswordHere',
        },
        body: JSON.stringify({ cvText: cv }),
      });
      if (!response.ok) throw new Error('Failed to analyse CV');

      const json = await response.json(); // { skills: [...] }
      setSkills(json.skills);
    } catch (err) {
      console.error('Failed to analyse CV:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // keep a stable context value to avoid re-renders of consumers
  const contextValue = useMemo(() => ({
    loading,
    jobs,
    skills,
    getJobs,
    deleteJob,
    saveJob,
    analyseCV
  }), [loading, jobs, skills, getJobs, deleteJob, saveJob, analyseCV]);

  useEffect(() => {
    getJobs();
  }, [getJobs]);

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = (): AuthContextType => {
  const context = useContext(ApiContext);
  if (!context) throw new Error('useApi must be used within ApiContext');
  return context;
};
